import { memo, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useHttpHook from "../../../Hooks/HttpHook";
import useUserHook from "../../../Hooks/userHook";
import { logout } from "../../../ReduxSlice/Auth/authSlice";
import { guardLogout } from "../../../ReduxSlice/Auth/guardSlice";
import {
  prependNotifications,
  updNotification,
} from "../../../ReduxSlice/Ui/UiSlice";
import {
  GetUserUnreadNotifications,
  MarkAllNotificationsAsRead,
  MarkNotificationAsRead,
} from "../../../utils/ApiConstant";
import { logoutPlatformUser } from "../../../utils/logoutUser";
import { timeSince, getImageUrl } from "../../../utils/Helper";
import { DEFAULT_PROFILE_IMAGE } from "../../../utils/brandAssets";
import { resolveApiUrl } from "../../../utils/apiBase";
import socket from "../../../utils/socketClient";
import InfiniteProgressBar from "../../Common/InfiniteProgressBar";
import { toast } from "react-toastify";
import {
  beginPlatformLogout,
  platformLogoutUrl,
  redirectUnauthorized,
} from "../../../utils/Helper";
import { showErrorToast } from "../../../utils/notify";

const siteUrl = (url = "") => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  return resolveApiUrl(raw).replace(/\/$/, "");
};

const MERCH_BASE = siteUrl(
  process.env.REACT_APP_MERCH_URL || process.env.REACT_APP_MERCH_URL_HOME || ""
);

const NAV_LINKS = [
  { label: "Home", href: MERCH_BASE },
  {
    label: "Social",
    platform: "social",
    href: siteUrl(process.env.REACT_APP_SOCIAL_API_URL),
  },
  {
    label: "Merch",
    platform: "merch",
    href: siteUrl(
      process.env.REACT_APP_MERCH_URL_HOME || process.env.REACT_APP_MERCH_URL
    ) || MERCH_BASE,
  },
  {
    label: "Music",
    platform: "music",
    href: siteUrl(process.env.REACT_APP_MUSIC_API_URL),
  },
  {
    label: "Danzflo TV",
    platform: "tv",
    href: siteUrl(process.env.REACT_APP_TV_API_URL),
  },
  { label: "Events", to: "/", active: true },
  {
    label: "Classes",
    platform: "class",
    href: siteUrl(process.env.REACT_APP_CLASSES_URL),
  },
];

const EventV2NavBar = () => {
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const [loading, sendRequest, error] = useHttpHook();
  const [bellToggleActive, setBellToggleActive] = useState(false);
  const [searchtext, setSearchText] = useState("");
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const { getProfilePic } = useUserHook();

  const { notifications, notificationCount } = useSelector(
    (state) => state.UiNotification
  );
  const { token, guardDetails } = useSelector((state) => state.guard);
  const { userDetails, masterToken, token: platformToken } = useSelector(
    (state) => state.user
  );
  const isGuardSession = Boolean(token) && !userDetails;
  const isAdminUser =
    String(userDetails?.userType || "").toLowerCase() === "admin";
  const guardDisplayName =
    guardDetails?.name ||
    guardDetails?.userName ||
    guardDetails?.email ||
    "Guard";

  const goToAdministrator = () => {
    setAccountOpen(false);
    setOffcanvasOpen(false);
    if (!masterToken) return;
    // Unified admin console SSO: /login/{double-base64(masterToken)}?deviceId=
    const adminBase = siteUrl(
      process.env.REACT_APP_ADMIN_URL || "https://admin.danzflo.com"
    );
    const authId = btoa(btoa(String(masterToken)));
    const deviceId = userDetails?.deviceId;
    const qs = deviceId
      ? `?deviceId=${encodeURIComponent(deviceId)}`
      : "";
    window.location.href = `${adminBase}/login/${authId}${qs}`;
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const onProfilePage = /\/[^/]+\/(userEvents|userPromotingEvents)(?:\/|$)/.test(
    location.pathname
  );
  const isAuthHandoff = /^\/auth(\/|$)/i.test(location.pathname);

  const query = new URLSearchParams(window.location.search);
  const title = query.get("title");
  const avatarFallback = DEFAULT_PROFILE_IMAGE;
  const rawProfileAvatar = getProfilePic() || "";
  const [avatarFailed, setAvatarFailed] = useState(false);
  const profileAvatar =
    rawProfileAvatar && !avatarFailed
      ? getImageUrl(rawProfileAvatar)
      : avatarFallback;

  useEffect(() => {
    setAvatarFailed(false);
  }, [rawProfileAvatar]);

  const renderAccountAvatar = () =>
    profileAvatar ? (
      <span className="portal-account-avatar-wrap" aria-hidden="true">
        <img
          className="portal-account-avatar"
          src={profileAvatar}
          alt=""
          onError={() => setAvatarFailed(true)}
        />
      </span>
    ) : (
      <span className="portal-account-avatar-icon" aria-hidden="true">
        <i className="bi bi-person-fill"></i>
      </span>
    );

  useEffect(() => {
    if (title) setSearchText(title);
  }, [title]);

  useEffect(() => {
    // The auth handoff clears any stale session itself. A navbar poll must
    // not bounce the user to merch login before SSO finishes.
    if (isAuthHandoff) return;
    if (error && toast.isActive(error) === false) {
      showErrorToast(error);
      // Guard sessions bounce to /guard/login; event sessions to merch login
      if (error === "You are unAuthrised") {
        redirectUnauthorized({ forceGuard: Boolean(token) });
      }
    }
  }, [error, token, isAuthHandoff]);

  useEffect(() => {
    if (!accountOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setAccountOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [accountOpen]);

  useEffect(() => {
    document.body.classList.toggle("event-nav-offcanvas-open", offcanvasOpen);
    return () => document.body.classList.remove("event-nav-offcanvas-open");
  }, [offcanvasOpen]);

  useEffect(() => {
    if (!offcanvasOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") setOffcanvasOpen(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [offcanvasOpen]);

  useEffect(() => {
    if (isAuthHandoff) return;
    if (userDetails && !token) getAllNotifications();
  }, [userDetails, token, isAuthHandoff]);

  useEffect(() => {
    const handleNotification = (data) => {
      dispatch(prependNotifications(data));
    };

    socket.on("notification", handleNotification);
    return () => {
      socket.off("notification", handleNotification);
    };
  }, [dispatch]);

  // Topbar always opens the logged-in user's destinations, even when the
  // sidebar is showing another (guest) social profile.
  const handlePlatformLink = (link, platform, e) => {
    e.preventDefault();
    const merchBase = (MERCH_BASE || link || "").replace(/\/$/, "");
    if (!masterToken) {
      window.location.href = platform === "merch" ? merchBase || link : link;
      return;
    }
    if (platform === "tv") {
      window.location.href = `${link}/index.php?link1=external_auth&authId=${encodeURIComponent(masterToken)}`;
    } else if (platform === "music") {
      window.location.href = `${link}/externalLogin?userName=${userDetails.userName}?authId=${masterToken}`;
    } else if (platform === "social") {
      window.location.href = `${link}/user/auth/login?authId=${masterToken}`;
    } else if (platform === "merch") {
      const loggedInUserName = userDetails?.userName;
      if (loggedInUserName) {
        window.location.href = `${merchBase}/auth/login/${masterToken}/${loggedInUserName}`;
      } else {
        window.location.href = link || merchBase;
      }
    } else if (platform === "class") {
      window.location.href = `${link}/auth/${masterToken}`;
    } else {
      window.location.href = link;
    }
  };

  const getAllNotifications = async () => {
    const res = await sendRequest(GetUserUnreadNotifications, { method: "GET" });
    if (res.status === 1) dispatch(updNotification(res.data));
  };

  const handleLogout = async () => {
    try {
      if (token) {
        dispatch(guardLogout());
        localStorage.clear();
        navigate("/guard/login");
        return;
      }
      const res = await logoutPlatformUser({
        masterToken,
        platformToken,
      });
      if (res.status >= 200 && res.status < 300) {
        beginPlatformLogout();
        dispatch(logout());
        dispatch(updNotification([]));
        dispatch(guardLogout());
        localStorage.clear();
        // Merch keeps its own localStorage; ?isLogout=true clears that session.
        // No redirect param — logout must land on login, not a return URL.
        window.location.replace(platformLogoutUrl());
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchtext) {
      navigate(`/search?title=${searchtext}`);
    } else {
      navigate(`/`);
    }
    window.location.reload();
  };

  const handleClickNotification = async (item) => {
    try {
      const res = await sendRequest(`${MarkNotificationAsRead}/${item._id}`, {
        method: "put",
      });
      if (res.status === 1) {
        if (item.message.includes("has placed a ticket cancel request")) {
          navigate(`/organizer/cancelTicket/requests?event=${item.eventId}`);
        } else if (item?.orderId) {
          navigate(`/myOrder/${item.orderId}`);
        } else if (item?.eventId) {
          navigate(`/organizer/myEarning/${item.eventId}`);
        } else {
          window.location.reload();
        }
      }
    } catch (err) {
      showErrorToast(err);
    }
  };

  const handleReadAllNotifications = async () => {
    const res = await sendRequest(MarkAllNotificationsAsRead, { method: "PUT" });
    if (res.status === 1) dispatch(updNotification([]));
  };

  const renderNotifications = () =>
    bellToggleActive ? (
      <div className="event-notif-dropdown">
        <div className="event-notif-head">
          <strong>Notifications</strong>
          <button
            type="button"
            className="btn-close btn-close-white btn-sm"
            onClick={() => setBellToggleActive(false)}
          />
        </div>
        <div className="event-notif-body">
          {notifications?.length > 0 ? (
            notifications.map((item, index) => (
              <button
                type="button"
                key={index}
                className="event-notif-item"
                onClick={() => handleClickNotification(item)}
              >
                <span>{item.message}</span>
                <small>{timeSince(item.createdAt)}</small>
              </button>
            ))
          ) : (
            <p className="event-notif-empty">No new notifications</p>
          )}
        </div>
        <div className="event-notif-foot">
          <Link to="/notifications" onClick={() => setBellToggleActive(false)}>
            View all
          </Link>
          <button type="button" onClick={handleReadAllNotifications}>
            Mark all read
          </button>
        </div>
      </div>
    ) : null;

  const renderBell = () => (
    <div className="position-relative portal-nav-notifications">
      <button
        type="button"
        className={`portal-header-icon-btn${bellToggleActive ? " is-active" : ""}`}
        aria-label={
          notificationCount
            ? `Notifications, ${
                notificationCount > 99 ? "99+" : notificationCount
              } unread`
            : "Notifications"
        }
        aria-expanded={bellToggleActive}
        aria-haspopup="menu"
        onClick={() => {
          setBellToggleActive((prev) => !prev);
          setAccountOpen(false);
        }}
      >
        <i className="bi bi-bell" aria-hidden="true"></i>
        {notificationCount > 0 && (
          <span
            className="portal-header-badge portal-header-badge--notify"
            aria-hidden="true"
          >
            {notificationCount > 99 ? "99+" : notificationCount}
          </span>
        )}
      </button>
      {renderNotifications()}
    </div>
  );

  const renderNavLink = (item, mobile = false) => {
    const className = `nav-link${item.active ? " active" : ""}`;
    if (item.to) {
      return (
        <li className="nav-item" key={item.label}>
          <Link
            className={className}
            to={item.to}
            onClick={(e) => {
              // Leaving a guest profile page via topbar Events → own hub.
              if (onProfilePage && item.to === "/") {
                e.preventDefault();
                const base = (
                  process.env.REACT_APP_BASE_URL || "/event"
                ).replace(/\/$/, "");
                window.location.assign(`${base}/`);
                return;
              }
              if (mobile) setOffcanvasOpen(false);
            }}
          >
            {item.label}
          </Link>
        </li>
      );
    }
    return (
      <li className="nav-item" key={item.label}>
        <a
          className={className}
          href={item.href}
          onClick={(e) => {
            if (item.platform) handlePlatformLink(item.href, item.platform, e);
            if (mobile) setOffcanvasOpen(false);
          }}
        >
          {item.label}
          {item.label === "Classes" && process.env.REACT_APP_BETA ? ` ${process.env.REACT_APP_BETA}` : ""}
        </a>
      </li>
    );
  };

  return (
    <>
      {loading && <InfiniteProgressBar />}
      <nav className="navbar navbar-dark sticky-top nav-glass nav-modern">
        <div className="container">
          <a
            className="navbar-brand d-flex align-items-center gap-2 fw-bold"
            href={MERCH_BASE}
            aria-label="Danzflo Home"
          >
            <img className="brand-logo" src="/event/assets/img/logo.png" alt="Danzflo logo" />
          </a>

          <div className="d-none d-lg-flex align-items-center portal-navbar-end">
            <ul className="navbar-nav flex-row align-items-center nav-links portal-navbar-links">
              {NAV_LINKS.map((item) => renderNavLink(item))}
            </ul>

            <form
              className="nav-search-form portal-search-form"
              role="search"
              aria-label="Search Danzflo"
              onSubmit={handleSearch}
            >
              <div className="input-group nav-search nav-search-compact">
                <span className="input-group-text">
                  <i className="bi bi-search" aria-hidden="true"></i>
                </span>
                <input
                  className="form-control"
                  type="search"
                  placeholder="Search..."
                  aria-label="Search"
                  value={searchtext}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </form>

            {userDetails && (
              <div className="portal-header-icons" aria-label="Event quick actions">
                {renderBell()}
              </div>
            )}

            {(userDetails || isGuardSession) && (
              <div className="portal-nav-account" ref={accountRef}>
                <button
                  type="button"
                  className={`portal-account-btn${
                    accountOpen ? " is-open" : ""
                  }`}
                  aria-expanded={accountOpen}
                  aria-haspopup="menu"
                  aria-label={isGuardSession ? "Guard account" : "My Account"}
                  onClick={() => {
                    setAccountOpen((prev) => !prev);
                    setBellToggleActive(false);
                  }}
                >
                  {renderAccountAvatar()}
                  <span className="portal-account-label">
                    {isGuardSession ? guardDisplayName : "My Account"}
                  </span>
                  <i
                    className="fas fa-caret-down portal-account-chevron"
                    aria-hidden="true"
                  />
                </button>
                {accountOpen && (
                  <div className="event-account-menu" role="menu">
                    {userDetails && (
                      <>
                        <button
                          type="button"
                          className="event-account-menu-item"
                          role="menuitem"
                          onClick={() => {
                            window.location.href = `${MERCH_BASE}/profile`;
                          }}
                        >
                          <i className="bi bi-person" aria-hidden="true"></i>
                          My Profile
                        </button>
                        <button
                          type="button"
                          className="event-account-menu-item"
                          role="menuitem"
                          onClick={() => {
                            navigate("/myOrders");
                            setAccountOpen(false);
                          }}
                        >
                          <i className="bi bi-basket" aria-hidden="true"></i>
                          Orders
                        </button>
                        <button
                          type="button"
                          className="event-account-menu-item"
                          role="menuitem"
                          onClick={() => {
                            navigate("/wishlist");
                            setAccountOpen(false);
                          }}
                        >
                          <i className="bi bi-heart" aria-hidden="true"></i>
                          Wishlists
                        </button>
                        <div
                          className="event-account-menu-divider"
                          aria-hidden="true"
                        />
                        <button
                          type="button"
                          className="event-account-menu-item"
                          role="menuitem"
                          onClick={() => {
                            window.location.href = `${process.env.REACT_APP_SOCIAL_API_URL}/user/auth/login?authId=${masterToken}&redirect=people`;
                          }}
                        >
                          <i className="bi bi-people" aria-hidden="true"></i>
                          People
                        </button>
                        {isAdminUser && (
                            <button
                              type="button"
                              className="event-account-menu-item"
                              role="menuitem"
                              onClick={goToAdministrator}
                            >
                              <i
                                className="bi bi-shield-lock"
                                aria-hidden="true"
                              ></i>
                              Administrator
                            </button>
                          )}
                      </>
                    )}
                    <button
                      type="button"
                      className="event-account-menu-item"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="portal-navbar-mobile d-flex d-lg-none">
            {userDetails && (
              <div className="portal-header-icons">
                {renderBell()}
              </div>
            )}
            <button
              className="navbar-toggler nav-toggler"
              type="button"
              aria-label="Open menu"
              onClick={() => {
                setBellToggleActive(false);
                setOffcanvasOpen(true);
              }}
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          </div>
        </div>
      </nav>

      {offcanvasOpen && (
        <>
          <button
            type="button"
            className="nav-offcanvas-backdrop"
            aria-label="Close menu"
            onClick={() => setOffcanvasOpen(false)}
          />
          <div
            className="nav-offcanvas"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            tabIndex={-1}
          >
            <div className="offcanvas-header">
              <img className="brand-logo" src="/event/assets/img/logo.png" alt="Danzflo logo" />
              <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={() => setOffcanvasOpen(false)} />
            </div>
            <div className="offcanvas-body d-flex flex-column">
              <form
                className="nav-search-form portal-search-form"
                role="search"
                aria-label="Search Danzflo"
                onSubmit={(e) => {
                  handleSearch(e);
                  setOffcanvasOpen(false);
                }}
              >
                <div className="input-group nav-search nav-search-compact w-100">
                  <span className="input-group-text">
                    <i className="bi bi-search" aria-hidden="true"></i>
                  </span>
                  <input
                    className="form-control"
                    type="search"
                    placeholder="Search..."
                    aria-label="Search"
                    value={searchtext}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </form>
              <div className="nav-divider" aria-hidden="true" />
              <nav aria-label="Mobile">
                <ul className="navbar-nav">{NAV_LINKS.map((item) => renderNavLink(item, true))}</ul>
              </nav>
            </div>
            {(userDetails || isGuardSession) && (
              <div className="nav-offcanvas-account">
                {userDetails && (
                  <>
                    <button
                      type="button"
                      className="event-account-btn event-account-btn--mobile"
                      onClick={() =>
                        (window.location.href = `${MERCH_BASE}/profile`)
                      }
                    >
                      {renderAccountAvatar()}
                      <span className="event-account-label">My Account</span>
                    </button>
                    {isAdminUser && (
                      <button
                        type="button"
                        className="nav-offcanvas-signout"
                        onClick={goToAdministrator}
                      >
                        <i className="bi bi-shield-lock" aria-hidden="true"></i>
                        Administrator
                      </button>
                    )}
                  </>
                )}
                {isGuardSession && (
                  <div className="event-account-btn event-account-btn--mobile">
                    {renderAccountAvatar()}
                    <span className="event-account-label">
                      {guardDisplayName}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  className="nav-offcanvas-signout"
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default memo(EventV2NavBar);
